"use client";

import type { ComponentType } from "react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { AppPending, AppShell, withLayout } from "./root";
import { WorkspaceSkeleton } from "@/components/workspace/WorkspaceSkeleton";
import { Navigate } from "@/lib/spa/navigation";
import { parseHref } from "@/lib/spa/href";
import { WEALTH_TABS } from "@/lib/nav/sectionTabs";
import { readWealthView, wealthResume } from "@/lib/wealth/viewState";

import TimingLayout from "@/app/timing/layout";
import SettingsLayout from "@/app/settings/layout";
import ScannerLayout from "@/app/scanner/layout";
import WealthLayout from "@/app/wealth/layout";
import OutlookLayout from "@/app/outlook/layout";
import LiveBreadthLayout from "@/app/live-breadth/layout";
import DailyBriefLayout from "@/app/daily-brief/layout";
import WorkspacesLayout from "@/app/workspaces/layout";
import MtfLayout from "@/app/mtf/layout";
import MultiChartLayout from "@/app/multi-chart/layout";
import ChartsLayout from "@/app/charts/layout";
import VpaLayout from "@/app/vpa/layout";
import FundamentalsLayout from "@/app/fundamentals/layout";
import SeasonalityLayout from "@/app/seasonality/layout";

import dynamic from "@/lib/spa/dynamic";

import LoginPage from "@/app/login/page";

const BacktestPage = dynamic(() => import("@/app/backtest/page"));
const MinerviniPage = dynamic(() => import("@/app/minervini/page"));
const StockbeePage = dynamic(() => import("@/app/stockbee/page"));
const CandlesticksPage = dynamic(() => import("@/app/candlesticks/page"));
const QuantPage = dynamic(() => import("@/app/quant/page"));
const RegimePage = dynamic(() => import("@/app/quant/regime/page"));
const LabPage = dynamic(() => import("@/app/lab/page"));
const LabAdvancedPage = dynamic(() => import("@/app/lab/chart-advanced/page"));
const LabTvPage = dynamic(() => import("@/app/lab/chart-tv/page"));
const LabPinePage = dynamic(() => import("@/app/lab/pine-editor/page"));

// ── Heavy routes are lazy, and the reason is measured ───────────────────
//
// A static import here puts the page's WHOLE import graph on the first-paint
// critical path, because the router is reachable from the entry. Measured on
// the 2026-09-15 build, `dist/client/index.html` preloaded 61 chunks =
// 3,865,281 B raw / 1,037,868 B gzip for EVERY route, and the four worst lines
// in this file accounted for most of it:
//
//   settings/indicators  1,115,451 B raw / 281,521 gzip — the 446-entry
//                        `lightweight-charts-indicators` library + the Pine
//                        runtime, pulled in by PineOverrideSettings, onto the
//                        critical path of a settings screen
//   mtf + multi-chart    1,011,740 B raw / 260,830 gzip — the entire Advanced
//                        Chart desk, which all 19 other call sites lazy-load
//   scanner                          38,744 gzip (+ the generated library
//                        columns) and it is what dragged StocksTablePane's
//                        43 kB into the entry chunk
//
// It also made `TopNav`'s idle prefetch of the chart chunk a no-op: it was
// "warming" a chunk the browser had already downloaded at boot.
//
// Routes stay static only when they are plausibly the first paint ("/", login,
// dashboard) or trivially small. Everything below is a secondary navigation
// where `route(..., { pending: true })` shows the skeleton the app already has.
const ScannerPage = dynamic(() => import("@/app/scanner/page"));
const LiveBreadthPage = dynamic(() => import("@/app/live-breadth/page"));
const MtfPage = dynamic(() => import("@/app/mtf/page"));
const MultiChartPage = dynamic(() => import("@/app/multi-chart/page"));
const TradesPage = dynamic(() => import("@/app/trades/page"));
const TradesRedirect = dynamic(() => import("@/app/trades/redirect"));
const WealthPage = dynamic(() => import("@/app/wealth/page"));
const WealthHoldingsPage = dynamic(() => import("@/app/wealth/holdings/page"));
const WealthPropertyPage = dynamic(() => import("@/app/wealth/property/page"));
const WealthTransactionsPage = dynamic(() => import("@/app/wealth/transactions/page"));
const WealthBudgetsPage = dynamic(() => import("@/app/wealth/budgets/page"));
const WealthSpendingPage = dynamic(() => import("@/app/wealth/spending/page"));
const WealthAccountsPage = dynamic(() => import("@/app/wealth/accounts/page"));
const WealthForecastPage = dynamic(() => import("@/app/wealth/forecast/page"));
const OptionsPage = dynamic(() => import("@/app/options/page"));
const BetsPage = dynamic(() => import("@/app/bets/page"));
const SettingsIndicatorsPage = dynamic(() => import("@/app/settings/indicators/page"));
const SettingsDataFeedPage = dynamic(() => import("@/app/settings/data-feed/page"));
const SettingsMaintenancePage = dynamic(() => import("@/app/settings/maintenance/page"));
const ChartsChartPage = dynamic(() => import("@/app/charts/chart/page"));
const VpaAnalysisPage = dynamic(() => import("@/app/vpa/analysis/page"));
const FundamentalsFinancialsPage = dynamic(
  () => import("@/app/fundamentals/financials/page"),
);
const SeasonalityCalendarPage = dynamic(() => import("@/app/seasonality/calendar/page"));

// Remaining routes, same reasoning as above: the router is reachable from the
// entry, so a static page import is a first-paint cost for every visitor
// regardless of where they navigated.
const FlowPage = dynamic(() => import("@/app/page"));
const FeaturesPage = dynamic(() => import("@/app/features/page"));
const DashboardPage = dynamic(() => import("@/app/dashboard/page"));
const FlowCenterPage = dynamic(() => import("@/app/flow/page"));
const HelpPage = dynamic(() => import("@/app/help/page"));
const HowToPage = dynamic(() => import("@/app/help/how-to/page"));
const AboutPage = dynamic(() => import("@/app/about/page"));
const PasswordPage = dynamic(() => import("@/app/account/password/page"));
const StockPage = dynamic(() => import("@/app/stocks/[symbol]/page"));
const CustomPage = dynamic(() => import("@/app/p/[slug]/page"));
const OutlookPage = dynamic(() => import("@/app/outlook/page"));
const DailyBriefPage = dynamic(() => import("@/app/daily-brief/page"));
const WorkspacesPage = dynamic(() => import("@/app/workspaces/page"));
const TimingIndexPage = dynamic(() => import("@/app/timing/page"));
const TimingBriefPage = dynamic(() => import("@/app/timing/timing-brief/page"));
const TimingBucketsPage = dynamic(() => import("@/app/timing/buckets/page"));
const TimingBreadth1Page = dynamic(() => import("@/app/timing/breadth-1/page"));
const TimingBreadth2Page = dynamic(() => import("@/app/timing/breadth-2/page"));
const TimingBreadth3Page = dynamic(() => import("@/app/timing/breadth-3/page"));
const TimingIndexEtfsPage = dynamic(() => import("@/app/timing/index-etfs/page"));
const TimingCommoditiesPage = dynamic(() => import("@/app/timing/commodities-etfs/page"));
const TimingGuppyPage = dynamic(() => import("@/app/timing/guppy/page"));
const TimingRatioPage = dynamic(() => import("@/app/timing/ratio/page"));
const TimingMacroPage = dynamic(() => import("@/app/timing/macro/page"));
const TimingCotPage = dynamic(() => import("@/app/timing/cot/page"));
const TimingCyclesPage = dynamic(() => import("@/app/timing/cycles/page"));
const TimingMarketMonitorPage = dynamic(() => import("@/app/timing/market-monitor/page"));
const Timing13fPage = dynamic(() => import("@/app/timing/13f/page"));
const TimingCalendarPage = dynamic(() => import("@/app/timing/calendar/page"));
const TimingLiveBreadthRedirect = dynamic(() => import("@/app/timing/live-breadth/page"));
const TimingDailyBriefRedirect = dynamic(() => import("@/app/timing/daily-brief/page"));
const TimingOutlookRedirect = dynamic(() => import("@/app/timing/outlook/page"));
const SettingsIndexPage = dynamic(() => import("@/app/settings/page"));
const SettingsIndicesPage = dynamic(() => import("@/app/settings/indices/page"));
const SettingsBreadthPage = dynamic(() => import("@/app/settings/breadth/page"));
const SettingsPagesPage = dynamic(() => import("@/app/settings/pages/page"));
const SettingsThemePage = dynamic(() => import("@/app/settings/theme/page"));
const SettingsBackupPage = dynamic(() => import("@/app/settings/backup/page"));
const ChartsIndexPage = dynamic(() => import("@/app/charts/page"));
const VpaIndexPage = dynamic(() => import("@/app/vpa/page"));
const FundamentalsIndexPage = dynamic(() => import("@/app/fundamentals/page"));
const SeasonalityIndexPage = dynamic(() => import("@/app/seasonality/page"));

const rootRoute = createRootRoute({
  component: AppShell,
  pendingComponent: AppPending,
});

function route(
  path: string,
  component: ComponentType,
  opts: { pending?: boolean; beforeLoad?: (ctx: { location: { searchStr: string } }) => void } = {},
) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path,
    component: component as never,
    pendingComponent: opts.pending ? WorkspaceSkeleton : undefined,
    beforeLoad: opts.beforeLoad,
  });
}

/** The page on screen: what the next navigation leaves. Null until the first page has loaded. */
let onScreen: string | null = null;

/**
 * "/wealth" from another section is "back to Wealth": it opens the tab that
 * was last open, with its view (lib/wealth/viewState.ts). From inside the
 * section it is the Overview tab.
 */
function resumeWealth({ location }: { location: { searchStr: string } }): void {
  const to = wealthResume({
    toSearch: location.searchStr,
    from: onScreen,
    saved: readWealthView().href,
    tabs: WEALTH_TABS.map((t) => t.href),
  });
  if (!to) return;
  const { pathname, search } = parseHref(to);
  throw redirect({ to: pathname as never, search: search as never, replace: true });
}

const routes = [
  route("/", FlowPage),
  route("/login", LoginPage),
  route("/features", FeaturesPage),
  route("/dashboard", DashboardPage, { pending: true }),
  route("/flow", FlowCenterPage, { pending: true }),
  route("/options", OptionsPage, { pending: true }),
  route("/bets", BetsPage, { pending: true }),
  route("/trades", TradesRedirect),
  route("/wealth", withLayout(WealthLayout, WealthPage), { pending: true, beforeLoad: resumeWealth }),
  route("/wealth/holdings", withLayout(WealthLayout, WealthHoldingsPage), { pending: true }),
  route("/wealth/property", withLayout(WealthLayout, WealthPropertyPage), { pending: true }),
  route("/wealth/transactions", withLayout(WealthLayout, WealthTransactionsPage), { pending: true }),
  route("/wealth/budgets", withLayout(WealthLayout, WealthBudgetsPage), { pending: true }),
  route("/wealth/spending", withLayout(WealthLayout, WealthSpendingPage), { pending: true }),
  route("/wealth/accounts", withLayout(WealthLayout, WealthAccountsPage), { pending: true }),
  route("/wealth/trades", withLayout(WealthLayout, TradesPage), { pending: true }),
  route("/wealth/forecast", withLayout(WealthLayout, WealthForecastPage), { pending: true }),
  route("/minervini", MinerviniPage, { pending: true }),
  route("/stockbee", StockbeePage, { pending: true }),
  route("/candlesticks", CandlesticksPage, { pending: true }),
  route("/backtest", BacktestPage, { pending: true }),
  route("/quant", QuantPage, { pending: true }),
  route("/quant/regime", RegimePage, { pending: true }),
  route("/help", HelpPage),
  route("/help/how-to", HowToPage),
  route("/about", AboutPage),
  route("/account/password", PasswordPage),
  route("/lab", LabPage),
  route("/lab/chart-advanced", LabAdvancedPage),
  route("/lab/chart-tv", LabTvPage),
  route("/lab/pine-editor", LabPinePage),
  route("/stocks/$symbol", StockPage, { pending: true }),
  route("/p/$slug", CustomPage, { pending: true }),
  route("/scanner", withLayout(ScannerLayout, ScannerPage), { pending: true }),
  route("/outlook", withLayout(OutlookLayout, OutlookPage), { pending: true }),
  route("/live-breadth", withLayout(LiveBreadthLayout, LiveBreadthPage), { pending: true }),
  route("/daily-brief", withLayout(DailyBriefLayout, DailyBriefPage), { pending: true }),
  route("/workspaces", withLayout(WorkspacesLayout, WorkspacesPage), { pending: true }),
  route("/mtf", withLayout(MtfLayout, MtfPage), { pending: true }),
  route("/multi-chart", withLayout(MultiChartLayout, MultiChartPage), { pending: true }),
  route("/timing", withLayout(TimingLayout, TimingIndexPage), { pending: true }),
  route("/timing/timing-brief", withLayout(TimingLayout, TimingBriefPage), { pending: true }),
  route("/timing/buckets", withLayout(TimingLayout, TimingBucketsPage), { pending: true }),
  route("/timing/breadth-1", withLayout(TimingLayout, TimingBreadth1Page), { pending: true }),
  route("/timing/breadth-2", withLayout(TimingLayout, TimingBreadth2Page), { pending: true }),
  route("/timing/breadth-3", withLayout(TimingLayout, TimingBreadth3Page), { pending: true }),
  route("/timing/index-etfs", withLayout(TimingLayout, TimingIndexEtfsPage), { pending: true }),
  route("/timing/commodities-etfs", withLayout(TimingLayout, TimingCommoditiesPage), { pending: true }),
  route("/timing/guppy", withLayout(TimingLayout, TimingGuppyPage), { pending: true }),
  route("/timing/ratio", withLayout(TimingLayout, TimingRatioPage), { pending: true }),
  route("/timing/macro", withLayout(TimingLayout, TimingMacroPage), { pending: true }),
  route("/timing/cot", withLayout(TimingLayout, TimingCotPage), { pending: true }),
  route("/timing/cycles", withLayout(TimingLayout, TimingCyclesPage), { pending: true }),
  route("/timing/market-monitor", withLayout(TimingLayout, TimingMarketMonitorPage), { pending: true }),
  route("/timing/13f", withLayout(TimingLayout, Timing13fPage), { pending: true }),
  route("/timing/calendar", withLayout(TimingLayout, TimingCalendarPage), { pending: true }),
  route("/timing/live-breadth", TimingLiveBreadthRedirect),
  route("/timing/daily-brief", TimingDailyBriefRedirect),
  route("/timing/outlook", TimingOutlookRedirect),
  route("/settings", withLayout(SettingsLayout, SettingsIndexPage)),
  route("/settings/data-feed", withLayout(SettingsLayout, SettingsDataFeedPage)),
  route("/settings/indices", withLayout(SettingsLayout, SettingsIndicesPage)),
  route("/settings/breadth", withLayout(SettingsLayout, SettingsBreadthPage)),
  route("/settings/pages", withLayout(SettingsLayout, SettingsPagesPage)),
  route("/settings/indicators", withLayout(SettingsLayout, SettingsIndicatorsPage)),
  route("/settings/theme", withLayout(SettingsLayout, SettingsThemePage)),
  route("/settings/backup", withLayout(SettingsLayout, SettingsBackupPage)),
  route("/settings/maintenance", withLayout(SettingsLayout, SettingsMaintenancePage)),
  route("/charts", withLayout(ChartsLayout, ChartsIndexPage), { pending: true }),
  route("/charts/chart", withLayout(ChartsLayout, ChartsChartPage), { pending: true }),
  route("/vpa", withLayout(VpaLayout, VpaIndexPage), { pending: true }),
  route("/vpa/analysis", withLayout(VpaLayout, VpaAnalysisPage), { pending: true }),
  route("/fundamentals", withLayout(FundamentalsLayout, FundamentalsIndexPage), { pending: true }),
  route("/fundamentals/financials", withLayout(FundamentalsLayout, FundamentalsFinancialsPage), { pending: true }),
  route("/seasonality", withLayout(SeasonalityLayout, SeasonalityIndexPage), { pending: true }),
  route("/seasonality/calendar", withLayout(SeasonalityLayout, SeasonalityCalendarPage), { pending: true }),
];

const routeTree = rootRoute.addChildren(routes);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPendingComponent: AppPending,
  defaultNotFoundComponent: () => <Navigate to="/flow" />,
});

router.subscribe("onResolved", (event) => {
  onScreen = event.toLocation.pathname;
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
